import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import { logout } from '../slices/authSlice';
import { logger } from '../../utils/logger';

// Tip tanımlamaları
import type { 
  LoginData, 
  RegisterData, 
  AuthResponse, 
  UserData, 
  VerifyEmailData, 
  ForgotPasswordData, 
  ResetPasswordData, 
  UpdateProfileData, 
  UpdatePasswordData 
} from '../../types/auth';
import type { User } from '../../api/user';
import type { Package, PaymentSession, PaymentHistory, PaymentHistoryResponse } from '../../types/billing';
import type { ApiResponse } from '../../types/api';
import type { UserStatistics, StatisticsResponse } from '../../types/statistics';
import type {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  CheckPasswordRequest,
  CheckPasswordResponse,
  ZipRequestResponse,
  ZipStatusResponse,
  EventDetailResponse,
  EventActionResponse,
  EventListResponse
} from '../../types/event';
import { 
  Photo,
  PhotoListResponse,
  PhotoUploadResponse 
} from '../../types/photos';

// Her kategori için endpoint'leri import edin
import { authApiEndpoints } from './authApi';
import { eventApiEndpoints } from './eventApi';
import { photoApiEndpoints } from './photoApi';
import { userApiEndpoints } from './userApi';
import { paymentApiEndpoints } from './paymentApi';
import { statisticsApiEndpoints } from './statisticsApi';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'https://ourphotos-backend.fly.dev/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQuery(args, api, extraOptions);
  
  if (result.error && 'status' in result.error) {
    const status = (result.error as { status: number }).status;
    
    // Token expired or invalid
    if (status === 401) {
      logger.debug('[API] Token expired or invalid, logging out');
      api.dispatch(logout());
    }
  }
  
  return result;
};

// Ana API tanımlaması
export const api = createApi({
  reducerPath: 'api',
  tagTypes: ['User', 'Auth', 'Packages', 'Statistics', 'Events', 'EventDetails', 'EventPhotos', 'QRCode', 'Event', 'Photos', 'Quota'],
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    // Farklı kategorilerdeki endpoint'leri birleştirelim
    ...authApiEndpoints(builder),
    ...userApiEndpoints(builder),
    ...eventApiEndpoints(builder),
    ...photoApiEndpoints(builder),
    ...paymentApiEndpoints(builder),
    ...statisticsApiEndpoints(builder),
  }),
});

export const enhancedApi = api.enhanceEndpoints({
  addTagTypes: ['User'],
});

// API middleware'i export et
export const apiMiddleware = api.middleware;

// Hook'ları doğrudan export edelim
export const {
  // Auth hooks
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useResendVerificationEmailMutation,
  
  // User hooks
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useUpdatePasswordMutation,
  
  // Event hooks
  useGetEventsQuery,
  useGetEventDetailsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useCheckEventPasswordMutation,
  useGenerateQRCodeQuery,
  useGetEventByUrlQuery,
  
  // Photo hooks
  useGetEventPhotosQuery,
  useGetPublicEventPhotosQuery,
  useRequestPhotosZipMutation,
  useCheckZipStatusQuery,
  useUploadGuestPhotoMutation,
  useUploadEventPhotoMutation,
  
  // Payment hooks
  useGetPackagesQuery,
  usePurchaseCreditsMutation,
  useCheckPaymentStatusQuery,
  useGetPaymentHistoryQuery,
  
  // Statistics hooks
  useGetUserStatsQuery,
  useGetUserStatisticsQuery,
  useGetEventStatisticsQuery,
} = api;
